import * as React from 'react'
import { Button } from '../../../shared-components/Button'
import { ErrorMessage } from '../../../shared-components/ErrorMessage'
import { InputText } from '../../../shared-components/InputText'
import { useScreenTracking } from '../../../shared-hooks/useScreenTracking'
import { track } from '../../../utils/analytics'
import { SignupCard } from '../SignupCard'
import { SSOStep, SSOStepProps } from './SSOFlow'

type EmailVerificationProps = SSOStepProps & {
  setSSOStep: React.Dispatch<React.SetStateAction<SSOStep>>
  sessionId: string
  emailHint: string
}

export const EmailVerification = ({
  setSSOStep,
  sessionId,
  emailHint,
  makeSSORequest,
}: EmailVerificationProps): JSX.Element => {
  useScreenTracking('EmailVerificationView', 'View email verification screen')

  const [emailCode, setEmailCode] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)
    setErrorMessage('')

    track('AttemptEmailVerification', 'Uber attempt email verification')

    const body = {
      session_id: sessionId,
      stage: 'sign_in',
      event_type: 'TypeEmailOTP',
      credentials: {
        email_code: emailCode,
      },
    }

    try {
      await makeSSORequest(body, 'Invalid pin entered. Please try again.')
      track('EmailVerificationSuccess', 'Email verification success')
    } catch (error) {
      track('EmailVerificationFailure', 'Email verification failure')
      setIsSubmitting(false)
      setErrorMessage(error?.message)
    }
  }

  return (
    <SignupCard
      heading='Enter 4 Digit Pin'
      subheading={`Uber will send you a 4 digit pin code to your email ${emailHint}. Enter it below.`}
    >
      <form className='flex flex-col gap-4' onSubmit={onSubmit}>
        <InputText
          pattern='\d{4}'
          placeholder='4 Digit Email Pin'
          autoComplete='off'
          // autoComplete='one-time-code' // Doesn't work on Chrome for some reason.
          value={emailCode}
          onChange={(event) => setEmailCode(event.target.value)}
          disabled={isSubmitting}
          maxLength={4}
          required
        />
        <ErrorMessage>{errorMessage}</ErrorMessage>
        <button
          type='button'
          className={`text-left text-sm font-extralight py-2 pl-1 rounded text-body-black hover-hover:hover:bg-blue-50 hover-hover:hover:bg-opacity-50 active:bg-blue-50 active:bg-opacity-50${
            errorMessage ? '' : ' -mt-6'
          }`}
          onClick={() => {
            track('UsePasswordInsteadClick', 'Use password instead click')
            setSSOStep(SSOStep.UberPassword)
          }}
        >
          Verify with password instead
        </button>
        <Button type='submit' isSubmitting={isSubmitting}>
          Continue
        </Button>
      </form>
    </SignupCard>
  )
}
